// /home/labdoo/zitech-nexus/frontend/src/pages/UserTrainings.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trainingAPI } from '../services/api';
import './UserTrainings.css';

// SVG Icons
const Icons = {
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" />
      <path d="M2 17L12 22L22 17" />
      <path d="M2 12L12 17L22 12" />
      <line x1="12" y1="12" x2="12" y2="7" />
      <circle cx="12" cy="19" r="1.5" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ut-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  ArrowRight: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  Calendar: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Check: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
      <path d="M20 6L9 17L4 12" />
    </svg>
  ),
  Progress: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  NotStarted: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  StatusDot: () => (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <circle cx="4" cy="4" r="4" />
    </svg>
  ),
  Plus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  Service: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  Technology: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" />
      <path d="M2 17L12 22L22 17" />
      <path d="M2 12L12 17L22 12" />
    </svg>
  ),
};

const UserTrainings: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState<any[]>([]);

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      const response = await trainingAPI.getMyEnrollments();
      setEnrollments(response.data);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'completed': return '#10b981';
      case 'in_progress': return '#0ea5e9';
      default: return '#f59e0b';
    }
  };

  const getStatusBackground = (status: string) => {
    switch(status) {
      case 'completed': return '#d1fae5';
      case 'in_progress': return '#dbeafe';
      default: return '#fef3c7';
    }
  };

  const getStatusText = (status: string) => {
    switch(status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      default: return 'Not Started';
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'completed': return <Icons.Check />;
      case 'in_progress': return <Icons.Progress />;
      default: return <Icons.NotStarted />;
    }
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 80) return '#10b981';
    if (percentage >= 50) return '#0ea5e9';
    if (percentage >= 25) return '#f59e0b';
    return '#ef4444';
  };

  const handleRowClick = (enrollmentId: number) => {
    navigate(`/dashboard/training/${enrollmentId}`);
  };

  const counts = {
    total: enrollments.length,
    completed: enrollments.filter(e => e.status === 'completed').length,
    inProgress: enrollments.filter(e => e.status === 'in_progress').length,
    notStarted: enrollments.filter(e => e.status === 'not_started').length,
  };

  if (loading) {
    return (
      <div className="ut-loading-container">
        <div className="ut-spinner">
          <Icons.Spinner />
        </div>
        <p className="ut-loading-text">Loading your trainings...</p>
      </div>
    );
  }

  return (
    <div className="ut-container">
      {/* Header */}
      <div className="ut-header">
        <div className="ut-header-left">
          <h1 className="ut-title">My Learning</h1>
          <p className="ut-subtitle">Track your progress in requested training programs</p>
        </div>
        <button 
          className="ut-btn-primary"
          onClick={() => navigate('/dashboard/request-service')}
        >
          <Icons.Plus />
          Request Service
        </button>
      </div>

      {/* Stats Bar */}
      <div className="ut-stats-bar">
        <div className="ut-stat-item">
          <span className="ut-stat-label">Total Enrollments</span>
          <span className="ut-stat-value">{counts.total}</span>
        </div>
        <div className="ut-stat-divider"></div>
        <div className="ut-stat-item">
          <span className="ut-stat-label">Completed</span>
          <span className="ut-stat-value ut-stat-completed">{counts.completed}</span>
        </div>
        <div className="ut-stat-divider"></div>
        <div className="ut-stat-item">
          <span className="ut-stat-label">In Progress</span>
          <span className="ut-stat-value ut-stat-progress">{counts.inProgress}</span>
        </div>
        <div className="ut-stat-divider"></div>
        <div className="ut-stat-item">
          <span className="ut-stat-label">Not Started</span>
          <span className="ut-stat-value ut-stat-notstarted">{counts.notStarted}</span>
        </div>
      </div>

      {/* Table */}
      <div className="ut-table-wrapper">
        {enrollments.length === 0 ? (
          <div className="ut-empty-state">
            <Icons.Empty />
            <h3 className="ut-empty-title">No Training Enrollments Yet</h3>
            <p className="ut-empty-text">Request and get approval for training programs to start learning.</p>
            <button className="ut-btn-secondary" onClick={() => navigate('/dashboard/request-service')}>
              <Icons.Plus />
              Request a Service
            </button>
          </div>
        ) : (
          <table className="ut-table">
            <thead>
              <tr>
                <th className="ut-col-tech">Technology</th>
                <th className="ut-col-service">Service</th>
                <th className="ut-col-progress">Progress</th>
                <th className="ut-col-status">Status</th>
                <th className="ut-col-started">Started</th>
                <th className="ut-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((enrollment) => (
                <tr 
                  key={enrollment.id} 
                  className="ut-table-row"
                  onClick={() => handleRowClick(enrollment.id)}
                >
                  <td className="ut-col-tech">
                    <span className="ut-tech-name">
                      <Icons.Technology />
                      {enrollment.technology || 'N/A'}
                    </span>
                  </td>
                  <td className="ut-col-service">
                    <span className="ut-service-name">
                      <Icons.Service />
                      {enrollment.service_name || 'Training Program'}
                    </span>
                  </td>
                  <td className="ut-col-progress">
                    <div className="ut-progress-wrapper">
                      <div className="ut-progress-bar">
                        <div 
                          className="ut-progress-fill"
                          style={{ 
                            width: `${enrollment.progress_percentage || 0}%`,
                            background: getProgressColor(enrollment.progress_percentage || 0)
                          }}
                        />
                      </div>
                      <span className="ut-progress-value">{enrollment.progress_percentage || 0}%</span>
                    </div>
                  </td>
                  <td className="ut-col-status">
                    <span 
                      className="ut-status-badge"
                      style={{
                        background: getStatusBackground(enrollment.status),
                        color: getStatusColor(enrollment.status)
                      }}
                    >
                      <span className="ut-status-icon">{getStatusIcon(enrollment.status)}</span>
                      {getStatusText(enrollment.status)}
                    </span>
                  </td>
                  <td className="ut-col-started">
                    <span className="ut-date-text">
                      <Icons.Calendar />
                      {enrollment.started_at 
                        ? new Date(enrollment.started_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })
                        : '-'}
                    </span>
                  </td>
                  <td className="ut-col-actions">
                    <button 
                      className="ut-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(enrollment.id);
                      }}
                    >
                      {enrollment.status === 'not_started' ? 'Start Learning' : 'Continue'}
                      <Icons.ArrowRight />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default UserTrainings;